import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getSalesOrder, getSalesOrderItem, getSalesOrderStatuses, type SalesOrderItem, saveSalesOrder, type SalesOrder } from "../../api/erp";
import { MDBBtn, MDBCard, MDBCardBody, MDBInput, MDBInputGroup, MDBTable, MDBTableBody, MDBTableHead } from "mdb-react-ui-kit";
import useLocale, { I18N } from "../../util/i18n";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlusCircle } from "@fortawesome/free-solid-svg-icons";
import Select, { type OptionType } from "../../component/select";
import { format } from "date-fns";
import Toast from "../../component/toast";
import { useCrumbs } from "../../component/breadcrumbs";
import currencies from "../../util/currencies.json";
import BigNumber from "bignumber.js";
import Autocomplete from "../../component/autocomplete";
import ExcelJS from "exceljs";

const DEFAULT_STATUSES = [
    { value: 'Placed', label: 'Placed' },
    { value: 'Complete', label: 'Complete' },
    { value: 'Deleted', label: 'Deleted' }
];

export default function SalesOrder(props: { editable: boolean }) {
    let { 0: query } = useSearchParams();
    let { 0: order, 1: setOrder } = React.useState<SalesOrder>({ id: '', order_id: '', created_at: 0, completed_at: 0, received_at: 0, subtotal: 0, tax: 0, total: 0, currency: 'USD', status: 'Placed' });
    let { 0: items, 1: setItems } = React.useState<Array<SalesOrderItem>>([]);
    let { 0: statuses, 1: setStatuses } = React.useState<Array<OptionType>>(DEFAULT_STATUSES);
    let { 0: __, 1: lang } = useLocale();
    let formatter = React.useMemo(() => new Intl.NumberFormat(lang, { style: 'currency', currency: order.currency || 'USD' }), [order, lang]);
    let { 0: setCrumbs } = useCrumbs();
    let navigate = useNavigate();
    let save = React.useCallback((e: React.FormEvent<HTMLFormElement>) => {
        e.stopPropagation();
        e.preventDefault();
        if (!(e.target as HTMLFormElement).checkValidity()) {
            return;
        }
        let subtotal = new BigNumber(0), tax = new BigNumber(0), total = new BigNumber(0);
        for (let i in items) {
            if (!parseFloat(items[i].rowtotal?.toString())) {
                items[i].rowtotal = (new BigNumber(items[i].unit_price)).times(items[i].qty).toNumber();
            }
            subtotal = subtotal.plus(items[i].rowtotal);
            tax = tax.plus(items[i].tax);
            total = total.plus(items[i].rowtotal).plus(items[i].tax);
        }
        if (!parseFloat(order.subtotal.toString())) {
            order.subtotal = subtotal.toNumber();
        }
        if (!parseFloat(order.tax.toString())) {
            order.tax = tax.toNumber();
        }
        if (!parseFloat(order.total.toString())) {
            order.total = total.toNumber();
        }
        saveSalesOrder({ ...order, item: items }).then(response => {
            if (typeof response !== 'string' && response.data) {
                let statuses: Array<OptionType> = JSON.parse(window.sessionStorage.getItem('SO-status') ?? '[]');
                if (statuses.filter(p => p.value === order.status).length === 0) {
                    statuses.push({ value: order.status, label: order.status });
                    setStatuses(statuses);
                }
                navigate('/sales-order/edit?id=' + response.data, { replace: true });
            }
            Toast.show('Data has been saved.');
        }, () => Toast.show(__('An error detected. Please try again later.')));
    }, [order, items, __]);
    let onChangeOrder = React.useCallback((e: { target: { name: string, value: string } } | string) => {
        setOrder(old => {
            if (typeof e === 'string') {
                return { ...old, status: e };
            } else if (e.target.name.endsWith('_at')) {
                return { ...old, [e.target.name]: Date.parse(e.target.value) / 1000 };
            } else {
                return { ...old, [e.target.name]: e.target.value };
            }
        });
    }, [setOrder]);
    let onChangeItem = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setItems(old => {
            const { 0: key, 1: index } = e.target.name.substring(0, e.target.name.length - 1).split('[');
            let cloned = [...old];
            //@ts-ignore
            cloned[parseInt(index)][key] = e.target.value;
            return cloned;
        });
    }, [setItems]);
    let add = React.useCallback((e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
        e.stopPropagation();
        e.preventDefault();
        setItems(old => ([...old, { id: '', sku: '', qty: 0, unit_price: 0, tax: 0, rowtotal: 0, comments: '', order_id: order.id }]));
    }, [setItems, order]);
    let deliver = React.useCallback((e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
        e.stopPropagation();
        e.preventDefault();
        navigate('/sales-shipment/edit?order_id=' + order.id);
    }, [order.id]);
    React.useEffect(() => {
        let id = query.get('id');
        if (id) {
            let cached = window.sessionStorage.getItem('SO-' + id);
            if (cached) {
                let order = JSON.parse(cached);
                setOrder(order);
                setCrumbs([{ label: 'Order', link: '/sales-order' }, order.order_id]);
            } else {
                getSalesOrder({ filter: { id }, limit: 1 }).then(response => {
                    if (typeof response !== 'string' && response.data.length) {
                        setCrumbs([{ label: 'Order', link: '/sales-order' }, response.data[0].order_id]);
                        setOrder(response.data[0]);
                    }
                });
            }
            getSalesOrderItem({ filter: { order_id: id }, limit: -1 }).then(response => {
                if (typeof response !== 'string') {
                    setItems(response.data);
                }
            });
        } else {
            setCrumbs([{ label: 'Order', link: '/sales-order' }, 'Edit']);
        }
        let statuses = window.sessionStorage.getItem('SO-status');
        if (statuses) {
            setStatuses(JSON.parse(statuses));
        } else {
            getSalesOrderStatuses().then(response => {
                let result = response.map(item => ({ value: item.status, label: item.status + '(' + item.count + ')' }));
                window.sessionStorage.setItem('SO-status', JSON.stringify(result));
                setStatuses(result);
            });
        }
    }, [query, setOrder, setItems, props.editable]);
    React.useEffect(() => {
        let l = () => {
            const workbook = new ExcelJS.Workbook();
            const sheet = workbook.addWorksheet();
            sheet.getCell(1, 1).value = __('Order ID');
            sheet.getCell(1, 2).value = order.order_id;
            sheet.getCell(2, 1).value = __('Currency');
            sheet.getCell(2, 2).value = order.currency;
            sheet.getCell(3, 1).value = __('Subtotal');
            sheet.getCell(3, 2).value = formatter.format(order.subtotal);
            sheet.getCell(4, 1).value = __('Tax');
            sheet.getCell(4, 2).value = formatter.format(order.tax);
            sheet.getCell(5, 1).value = __('Total');
            sheet.getCell(5, 2).value = formatter.format(order.total);
            sheet.getCell(6, 1).value = __('Ordered at');
            sheet.getCell(6, 2).value = format(order.created_at * 1000, 'yyyy/MM/dd');
            sheet.getCell(7, 1).value = __('Received at');
            sheet.getCell(7, 2).value = order.received_at > 0 ? format(order.received_at * 1000, 'yyyy/MM/dd') : '';
            sheet.getCell(8, 1).value = __('Completed at');
            sheet.getCell(8, 2).value = order.completed_at > 0 ? format(order.completed_at * 1000, 'yyyy/MM/dd') : '';
            sheet.getCell(9, 1).value = __('Status');
            sheet.getCell(9, 2).value = order.status;
            sheet.getCell(10, 1).value = 'SKU';
            sheet.getCell(10, 2).value = __('Qty');
            sheet.getCell(10, 3).value = __('Unit Price');
            sheet.getCell(10, 4).value = __('Tax');
            sheet.getCell(10, 5).value = __('Grand Total');
            sheet.getCell(10, 6).value = __('Comment');
            sheet.getCell(1, 1).style.font = sheet.getCell(2, 1).style.font = sheet.getCell(3, 1).style.font = sheet.getCell(4, 1).style.font =
                sheet.getCell(5, 1).style.font = sheet.getCell(6, 1).style.font = sheet.getCell(7, 1).style.font = sheet.getCell(8, 1).style.font = sheet.getCell(9, 1).style.font =
                sheet.getCell(10, 1).style.font = sheet.getCell(10, 2).style.font = sheet.getCell(10, 3).style.font = sheet.getCell(10, 4).style.font = sheet.getCell(10, 5).style.font = sheet.getCell(10, 6).style.font = { bold: true };
            for (let r = 0; r < items.length; r++) {
                let row = sheet.getRow(11 + r);
                row.getCell(1).value = items[r].sku;
                row.getCell(2).value = parseFloat(items[r].qty.toString());
                row.getCell(3).value = formatter.format(items[r].unit_price);
                row.getCell(4).value = formatter.format(items[r].tax);
                row.getCell(5).value = formatter.format(items[r].rowtotal);
                row.getCell(6).value = items[r].comments;
            }
            workbook.xlsx.writeBuffer().then(buffer => {
                let a = document.createElement('a');
                a.download = order.order_id + '.xlsx';
                a.href = URL.createObjectURL(new Blob([buffer]));
                document.body.append(a);
                a.click();
                URL.revokeObjectURL(a.href);
                a.remove();
            });
        };
        document.addEventListener('export', l);
        return () => {
            document.removeEventListener('export', l);
        };
    }, [order, items, formatter]);
    return (
        <form className="edit h-100" onSubmit={save} data-exportable>
            <div className="accordion">
                <MDBCard className="mb-3">
                    <MDBCardBody>
                        <input type="hidden" name="id" value={order.id} />
                        <MDBInput label={__('Order ID')} required readOnly={!props.editable} wrapperClass="mb-3" className="active" name="order_id" value={order.order_id} onChange={onChangeOrder} />
                        <Select label={__('Currency')} required readOnly={!props.editable} options={currencies.map(value => ({ value, label: __(value) }))} value={order.currency || 'USD'} onChange={onChangeOrder} wrapperClass="mb-3"></Select>
                        <MDBInput type="number" label={__('Subtotal')} min="0" step="0.0001" readOnly={!props.editable} wrapperClass="mb-3" className="active" name="subtotal" value={order.subtotal} onChange={onChangeOrder} />
                        <MDBInput type="number" label={__('Tax')} min="0" step="0.0001" readOnly={!props.editable} wrapperClass="mb-3" className="active" name="tax" value={order.tax} onChange={onChangeOrder} />
                        <MDBInput type="number" label={__('Grand Total')} min="0" step="0.0001" readOnly={!props.editable} wrapperClass="mb-3" className="active" name="total" value={order.total} onChange={onChangeOrder} />
                        <MDBInput type="date" label={__('Ordered at')} required readOnly={!props.editable} wrapperClass="mb-3" className="active" name="ordered_at" value={format((order.created_at || Date.now() / 1000) * 1000, 'yyyy-MM-dd')} onChange={onChangeOrder} />
                        <MDBInput type="date" label={__('Received at')} readOnly={!props.editable} wrapperClass="mb-3" className="active" name="received_at" value={order.received_at ? format(order.received_at * 1000, 'yyyy-MM-dd') : ''} onChange={onChangeOrder} />
                        <MDBInput type="date" label={__('Completed at')} readOnly={!props.editable} wrapperClass="mb-3" className="active" name="completed_at" value={order.completed_at ? format(order.completed_at * 1000, 'yyyy-MM-dd') : ''} onChange={onChangeOrder} />
                        <Autocomplete label={__('Status')} required readOnly={!props.editable} name="status" value={order.status} options={statuses?.length ? statuses : DEFAULT_STATUSES} onChange={onChangeOrder} />
                    </MDBCardBody>
                </MDBCard>
                <MDBCard>
                    <MDBCardBody>
                        <MDBTable>
                            <MDBTableHead>
                                <tr>
                                    <th>SKU</th>
                                    <th className="text-nowrap text-center">{__('Qty')}</th>
                                    <th className="text-nowrap text-center">{__('Subtotal')}</th>
                                    <th className="text-nowrap text-center">{__('Tax')}</th>
                                    <th className="text-nowrap text-center">{__('Row Total')}</th>
                                    <th className="text-nowrap text-center">{__('Comments')}</th>
                                </tr>
                            </MDBTableHead>
                            {props.editable ? (
                                <>
                                    <MDBTableBody>
                                        {items.map((item, i) => (
                                            <tr key={item.id + i.toString()}>
                                                <th>
                                                    <input type="hidden" name={'id[' + i + ']'} value={item.id} />
                                                    <MDBInput name={'sku[' + i + ']'} value={item.sku} onChange={onChangeItem} />
                                                </th>
                                                <td><MDBInput type="number" min="0" step="0.0001" name={'qty[' + i + ']'} value={item.qty} onChange={onChangeItem} /></td>
                                                <td>
                                                    <MDBInputGroup textBefore={order.currency}>
                                                        <MDBInput type="number" min="0" step="0.0001" name={'unit_price[' + i + ']'} value={item.unit_price} onChange={onChangeItem} />
                                                    </MDBInputGroup>
                                                </td>
                                                <td>
                                                    <MDBInputGroup textBefore={order.currency}>
                                                        <MDBInput type="number" min="0" step="0.0001" name={'tax[' + i + ']'} value={item.tax} onChange={onChangeItem} />
                                                    </MDBInputGroup>
                                                </td>
                                                <td>
                                                    <MDBInputGroup textBefore={order.currency}>
                                                        <MDBInput type="number" min="0" step="0.0001" name={'rowtotal[' + i + ']'} value={item.rowtotal} onChange={onChangeItem} />
                                                    </MDBInputGroup>
                                                </td>
                                                <td><MDBInput name={'comments[' + i + ']'} value={item.comments} onChange={onChangeItem} /></td>
                                            </tr>
                                        ))}
                                    </MDBTableBody>
                                    <tfoot>
                                        <tr>
                                            <td colSpan={6}>
                                                <MDBBtn color="light" block onClick={add}>
                                                    <FontAwesomeIcon icon={faPlusCircle} size="2x" />
                                                </MDBBtn>
                                            </td>
                                        </tr>
                                    </tfoot>
                                </>
                            ) : (
                                <MDBTableBody>
                                    {items.map(item => (
                                        <tr key={item.id}>
                                            <th>{item.sku}</th>
                                            <td className="text-center">{parseFloat(item.qty.toString())}</td>
                                            <td className="text-center">{formatter.format(item.unit_price || 0)}</td>
                                            <td className="text-center">{formatter.format(item.tax || 0)}</td>
                                            <td className="text-center">{formatter.format(item.rowtotal || 0)}</td>
                                            <td className="text-center">{item.comments}</td>
                                        </tr>
                                    ))}
                                </MDBTableBody>
                            )}
                        </MDBTable>
                    </MDBCardBody>
                </MDBCard>
            </div>
            {props.editable ? (
                <MDBCard className="tools">
                    <MDBCardBody>
                        <div className="btns">
                            {order.id ? (
                                <MDBBtn color="success" type="button" onClick={deliver}><I18N>Deliver</I18N></MDBBtn>
                            ) : null}
                            <MDBBtn color="info" type="reset"><I18N>Reset</I18N></MDBBtn>
                            <MDBBtn color="primary" type="submit"><I18N>Save</I18N></MDBBtn>
                        </div>
                    </MDBCardBody>
                </MDBCard>
            ) : null}
        </form>
    );
}